import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, API, CommonSiteService, SiteFilter, Site, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios, Client } from '@umanforge/common';
import { ModifySite, PostSite } from 'interfaces/api_site.interfaces';
import { ImportError } from 'interfaces/api_import.interfaces';
import { isHeaderProblem } from 'utils/import.utils';


export class SiteService {

  static getSite(
    siteId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Site> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSiteService.getSite( siteId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedSites(
    filters: SiteFilter | null,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Site[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSiteService.getAllowedSites( filters, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedSitesByClientsId(
    clientsId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Site[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonSiteService.getAllowedSitesByClientsId( clientsId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static postSite(
    body: PostSite,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    return new Promise( async resolve => {
      AXIOS.post( API.SITES, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 ? response.data.id : null );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

  static deleteSite(
    siteId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SITES + siteId, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static putSite(
    siteId: UUID, body: ModifySite,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.SITES + siteId, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static postAdminsToSite(
    siteId: UUID, body: { admins: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.SITES + siteId + '/admins', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteAdminsToSite(
    siteId: UUID, body: { admins: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.SITES + siteId + '/admins', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static putAllocatingCloudStorage(
    body: { cloudStorages: { siteId: UUID, allocatedSpace: number }[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.SITES + 'allocating_cloud_storage', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static exportSitesOfClient(
    client: Client,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.SITES }export/${ client.id }`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = `${ client.name }_sites_export.xlsx`;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static downloadSitesTemplate(
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.SITES }export`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = 'sites_template.xlsx';
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static importSitesOnClient(
    clientId: UUID, file: File, hasHeader: boolean | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID[] | ImportError | null> {
    return new Promise( async resolve => {

      const formData = new FormData();
      formData.append( 'content', file );
      if ( hasHeader !== undefined ) {
        formData.append( 'hasHeader', String( hasHeader ) );
      }
      const requestConfig = await CommonGlobalService.getHeaderRequest( token );
      ( requestConfig.headers ?? {} )[ 'Content-Type' ] = 'multipart/form-data';

      AXIOS.post( `${ API.SITES }import/${ clientId }`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          let error: ImportError | null = null;
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 && !( isHeaderProblem( response.data ) && hasHeader === undefined ) ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 202 ) {
            error = response.data as ImportError;
          }

          resolve( response.status === 200 ? response.data.ids : error );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

};